import { isUndefined, some } from "lodash";
import { useMemo } from "react";

import { addCustomerNote, deleteCustomerNote, updateCustomerNote } from "components/CustomerModal/components/CustomerNotes/hooks";
import { typedQueryClient } from "hooks";
import { TYRE_LOCATIONS } from "models";
import { setConnectedInterventionIdToFilter, setConnectedQuestionResultIdToFilter } from "modules/AppointmentDetails/hooks";
import { AppointmentDetailsKeys } from "modules/AppointmentDetails/queryKeys";
import { detachTyreReplacementsFromIntervention } from "modules/AppointmentDetails/utils";

import type { WebSocketMessageListener } from "components";
import type { UpdateCustomerPayload } from "components/CustomerModal/components/CustomerNotes/hooks";
import type { MandatoryTypedQueryKey } from "hooks";
import type {
  AcsesCommunication,
  AcsesCommunicationReceiver,
  Appointment,
  AppointmentLog,
  AppointmentNote,
  AppointmentNoteAttachment,
  AppointmentStatusHistory,
  Car,
  CarCheckEvent,
  CarNote,
  CarNoteAttachment,
  CheckInRemark,
  CheckInResult,
  Checklist,
  CommunicationAgreement,
  CommunicationEvent,
  CommunicationReceiver,
  CommunicationResult,
  Customer,
  CustomerCommunication,
  CustomerCommunicationIntervention,
  CustomerCommunicationReplacement,
  CustomerNote,
  DeskCommunication,
  DeskCommunicationEvent,
  DiagnoseOverviewAgreedResult,
  DiagnoseOverviewContactResult,
  DiagnoseOverviewDeclinedResult,
  DiagnoseOverviewRemark,
  Intervention,
  InterventionAttachment,
  InterventionElement,
  InterventionPendingChecklist,
  KeyLockerCommunication,
  KeyLockerCommunicationEvent,
  KeyLockerCommunicationReceiver,
  KeyLockerRemark,
  KioskCommunication,
  KioskCommunicationEvent,
  KioskRemark,
  Pin,
  Snooze,
  VehicleTyre,
  VehicleTyreReplacement
} from "models";

const getUpdatedCustomer = (customer: Customer, customerNote: CustomerNote) => {
  const noteIdx = customer.notes?.findIndex(cn => cn.id === customerNote.id) ?? -1;
  const updateFn = noteIdx < 0 ? addCustomerNote : updateCustomerNote;
  return updateFn(customer, customerNote);
};

export const useAppointmentListeners = (appointment_id: number, queryKey: MandatoryTypedQueryKey<Appointment>) => {
  const listeners = useMemo(() => {
    if (!appointment_id) return [];

    const appointmentListeners: WebSocketMessageListener<Appointment>[] = [
      {
        model: "Appointment",
        action: "upsert",
        id: appointment_id,
        callback: ({ data: appointmentData }) => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment) return;

          typedQueryClient.setQueryData(queryKey, { ...appointment, ...appointmentData });
        }
      }
    ];

    const interventionElementListeners: WebSocketMessageListener<InterventionElement>[] = [
      {
        model: "InterventionElement",
        action: "upsert",
        filter: { appointment_id },
        callback: ({ data: element }) => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment || !appointment.interventions?.length) return;

          const interventionIdx = appointment.interventions.findIndex(intervention => intervention.id === element.intervention_id);
          if (interventionIdx < 0) return;

          const elements = [...(appointment.interventions[interventionIdx].elements ?? [])];
          const elementIdx = elements.findIndex(e => e.id === element.id);
          if (elementIdx < 0) elements.push(element);
          else elements[elementIdx] = { ...elements[elementIdx], ...element };

          typedQueryClient.setQueryData(queryKey, {
            ...appointment,
            interventions: appointment.interventions.with(interventionIdx, { ...appointment.interventions[interventionIdx], elements })
          });
        }
      },
      {
        model: "InterventionElement",
        action: "delete",
        filter: { appointment_id },
        callback: ({ data: element }) => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment || !appointment.interventions?.length) return;

          const interventionIdx = appointment.interventions.findIndex(intervention => intervention.id === element.intervention_id);
          if (interventionIdx < 0 || !appointment.interventions[interventionIdx].elements) return;

          const elementIdx = appointment.interventions[interventionIdx].elements.findIndex(e => e.id === element.id);
          if (elementIdx < 0) return;

          typedQueryClient.setQueryData(queryKey, {
            ...appointment,
            interventions: appointment.interventions.with(interventionIdx, {
              ...appointment.interventions[interventionIdx],
              elements: appointment.interventions[interventionIdx].elements.toSpliced(elementIdx, 1)
            })
          });
        }
      }
    ];

    const carListeners: WebSocketMessageListener<Car>[] = [
      {
        model: "Car",
        action: "upsert",
        callback: message => {
          const car = message.data;
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (appointment?.car_id !== car.id || !appointment.car) return;

          typedQueryClient.setQueryData(queryKey, { ...appointment, car: { ...appointment.car, ...car } });
        }
      }
    ];

    const pinListeners: WebSocketMessageListener<Pin>[] = [
      {
        model: "Pin",
        action: "upsert",
        filter: { appointment_id },
        callback: ({ data: pin }) => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment?.interventions?.length) return;

          const interventionIdx = appointment.interventions.findIndex(i => i.id === pin.intervention_id);
          if (interventionIdx < 0) return;

          const intervention = appointment.interventions[interventionIdx];
          const pinHistoryIdx = intervention.pin_history?.findIndex(p => p.id === pin.id) ?? -1;

          let updatedPinHistory = [...(intervention.pin_history || []), pin];
          if (pinHistoryIdx > -1 && intervention.pin_history?.length) {
            updatedPinHistory = intervention.pin_history.with(pinHistoryIdx, { ...intervention.pin_history[pinHistoryIdx], ...pin });
          }

          typedQueryClient.setQueryData(queryKey, {
            ...appointment,
            interventions: appointment.interventions.with(interventionIdx, { ...intervention, pin_history: updatedPinHistory })
          });
        }
      },
      {
        model: "Pin",
        action: "delete",
        filter: { appointment_id },
        callback: message => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          const interventions = appointment?.interventions;
          if (!interventions?.length || !appointment) return;

          const pin = message.data;
          const interventionIdx = interventions.findIndex(i => i.id === pin.intervention_id);
          if (interventionIdx < 0) return;

          const intervention = interventions[interventionIdx];
          const history = intervention.pin_history;
          if (!history?.length) return;

          const pinIdx = history.findIndex(p => p.id === pin.id);
          if (pinIdx < 0) return;

          typedQueryClient.setQueryData(queryKey, {
            ...appointment,
            interventions: interventions.with(interventionIdx, { ...intervention, pin_history: history.toSpliced(pinIdx, 1) })
          });
        }
      }
    ];

    const appointmentNoteListeners: WebSocketMessageListener<AppointmentNote>[] = [
      {
        model: "AppointmentNote",
        action: "upsert",
        filter: { appointment_id },
        callback: message => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment) return;

          const appointmentNote = message.data;
          const appointmentNoteIdx = appointment.notes?.findIndex(n => n.id === appointmentNote.id) ?? -1;

          let updatedNotes = [...(appointment.notes ?? []), appointmentNote];
          if (appointmentNoteIdx > -1 && appointment.notes?.length) {
            updatedNotes = appointment.notes.with(appointmentNoteIdx, { ...appointment.notes[appointmentNoteIdx], ...appointmentNote });
          }

          typedQueryClient.setQueryData(queryKey, { ...appointment, notes: updatedNotes });
        }
      },
      {
        model: "AppointmentNote",
        action: "delete",
        filter: { appointment_id },
        callback: message => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment?.notes?.length) return;

          const appointmentNote = message.data;
          const appointmentNoteIdx = appointment.notes.findIndex(n => n.id === appointmentNote.id);
          if (appointmentNoteIdx < 0) return;

          typedQueryClient.setQueryData(queryKey, { ...appointment, notes: appointment.notes.toSpliced(appointmentNoteIdx, 1) });
        }
      }
    ];

    const appointmentNoteAttachmentListeners: WebSocketMessageListener<AppointmentNoteAttachment>[] = [
      {
        model: "AppointmentNoteAttachment",
        action: "delete",
        filter: { appointment_id },
        callback: ({ data: attachment }) => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment?.notes?.length) return;

          const noteIdx = appointment.notes.findIndex(n => n.id === attachment.appointment_note_id);
          if (noteIdx < 0) return;

          const note = appointment.notes[noteIdx];
          const attachmentIdx = note.attachments?.findIndex(a => a.id === attachment.id) ?? -1;
          if (attachmentIdx < 0) return;

          typedQueryClient.setQueryData(queryKey, {
            ...appointment,
            notes: appointment.notes.with(noteIdx, { ...note, attachments: note.attachments?.toSpliced(attachmentIdx, 1) })
          });
        }
      }
    ];

    const interventionListeners: WebSocketMessageListener<Intervention>[] = [
      {
        model: "Intervention",
        action: "upsert",
        filter: { appointment_id },
        callback: ({ data: intervention }) => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment) return;

          const interventions = appointment.interventions ?? [];
          const interventionIdx = interventions.findIndex(i => i.id === intervention.id);

          const tyres = [...(appointment.car?.tyres ?? [])];
          intervention.replacements?.forEach(replacement => {
            const tyreIdx = tyres.findIndex(t => t.replacements?.some(r => r.id === replacement.id));
            if (tyreIdx > -1 && tyres[tyreIdx].replacements?.length) {
              const replacementIdx = tyres[tyreIdx].replacements.findIndex(r => r.id === replacement.id);
              if (replacementIdx > -1)
                tyres[tyreIdx] = {
                  ...tyres[tyreIdx],
                  replacements: tyres[tyreIdx].replacements.with(replacementIdx, { ...tyres[tyreIdx].replacements[replacementIdx], intervention_id: intervention.id })
                };
            }
          });

          typedQueryClient.setQueryData(queryKey, {
            ...appointment,
            car: appointment.car ? { ...appointment.car, tyres } : undefined,
            interventions:
              interventionIdx < 0 ? interventions.concat(intervention) : interventions.with(interventionIdx, { ...interventions[interventionIdx], ...intervention })
          });
        }
      },
      {
        model: "Intervention",
        action: "delete",
        filter: { appointment_id },
        callback: ({ data: intervention }) => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment?.interventions?.length) return;

          const interventionIdx = appointment.interventions.findIndex(i => i.id === intervention.id);
          if (interventionIdx < 0) return;

          typedQueryClient.setQueryData(queryKey, {
            ...appointment,
            car: appointment.car ? { ...appointment.car, tyres: detachTyreReplacementsFromIntervention(intervention.id, appointment.car.tyres) } : appointment.car,
            interventions: appointment.interventions.toSpliced(interventionIdx, 1)
          });
          setConnectedInterventionIdToFilter(null);
          setConnectedQuestionResultIdToFilter(null);
        }
      }
    ];

    const keyLockerCommunicationListeners: WebSocketMessageListener<KeyLockerCommunication>[] = [
      {
        model: "KeyLockerCommunication",
        action: "upsert",
        filter: { appointment_id },
        callback: ({ data: keyLockerComm }) => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment) return;

          const keyLockerCommIdx = appointment.keylocker_communications?.findIndex(k => k.id === keyLockerComm.id) ?? -1;

          typedQueryClient.setQueryData(queryKey, {
            ...appointment,
            keylocker_communications:
              keyLockerCommIdx > -1 && appointment.keylocker_communications
                ? appointment.keylocker_communications.with(keyLockerCommIdx, { ...appointment.keylocker_communications[keyLockerCommIdx], ...keyLockerComm })
                : [...(appointment.keylocker_communications ?? []), keyLockerComm]
          });
        }
      }
    ];

    const keyLockerCommunicationEventListeners: WebSocketMessageListener<KeyLockerCommunicationEvent>[] = [
      {
        model: "KeyLockerCommunicationEvent",
        action: "upsert",
        filter: { appointment_id },
        callback: ({ data: keyLockerCommEvent }) => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment?.keylocker_communications?.length) return;

          const keyLockerCommunicationIdx = appointment.keylocker_communications.findIndex(kc => kc.id === keyLockerCommEvent.keylocker_communication_id);
          if (keyLockerCommunicationIdx < 0) return;

          const keyLockerEvents = appointment.keylocker_communications[keyLockerCommunicationIdx].events;
          const keyLockerEventIdx = keyLockerEvents?.findIndex(e => e.id === keyLockerCommEvent.id) ?? -1;

          typedQueryClient.setQueryData(queryKey, {
            ...appointment,
            keylocker_communications: appointment.keylocker_communications.with(keyLockerCommunicationIdx, {
              ...appointment.keylocker_communications[keyLockerCommunicationIdx],
              events:
                keyLockerEventIdx > -1 && keyLockerEvents
                  ? keyLockerEvents.with(keyLockerEventIdx, { ...keyLockerEvents[keyLockerEventIdx], ...keyLockerCommEvent })
                  : [...(keyLockerEvents ?? []), keyLockerCommEvent]
            })
          });
        }
      }
    ];

    const keyLockerCommunicationReceiverListeners: WebSocketMessageListener<KeyLockerCommunicationReceiver>[] = [
      {
        model: "KeyLockerCommunicationReceiver",
        action: "upsert",
        filter: { appointment_id },
        callback: ({ data: keyLockerReceiver }) => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment?.keylocker_communications?.length) return;

          const keyLockerCommunicationIdx = appointment.keylocker_communications.findIndex(kc =>
            kc.events?.some(e => e.id === keyLockerReceiver.keylocker_communication_id)
          );
          if (keyLockerCommunicationIdx < 0) return;

          const keyLockerCommunication = appointment.keylocker_communications[keyLockerCommunicationIdx];
          const keyLockerEvents = keyLockerCommunication.events ?? [];
          const keyLockerEventIdx = keyLockerEvents.findIndex(e => e.id === keyLockerReceiver.keylocker_communication_id);
          if (keyLockerEventIdx < 0) return;

          const keyLockerEvent = keyLockerEvents[keyLockerEventIdx];
          const receivers = keyLockerEvent.receivers ?? [];
          const receiverIdx = receivers.findIndex(r => r.id === keyLockerReceiver.id);

          typedQueryClient.setQueryData(queryKey, {
            ...appointment,
            keylocker_communications: appointment.keylocker_communications.with(keyLockerCommunicationIdx, {
              ...keyLockerCommunication,
              events: keyLockerEvents.with(keyLockerEventIdx, {
                ...keyLockerEvent,
                receivers: receiverIdx > -1 ? receivers.with(receiverIdx, { ...receivers[receiverIdx], ...keyLockerReceiver }) : [...receivers, keyLockerReceiver]
              })
            })
          });
        }
      }
    ];

    const keyLockerRemarkListeners: WebSocketMessageListener<KeyLockerRemark>[] = [
      {
        model: "KeyLockerRemark",
        action: "upsert",
        filter: { appointment_id },
        callback: message => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment?.keylocker_communications?.length) return;

          const remark = message.data;
          const commIdx = appointment.keylocker_communications.findIndex(kc => kc.id === remark.keylocker_communication_id);
          if (commIdx < 0) return;

          const comm = appointment.keylocker_communications[commIdx];
          typedQueryClient.setQueryData(queryKey, {
            ...appointment,
            keylocker_communications: appointment.keylocker_communications.with(commIdx, { ...comm, remark: { ...comm.remark, ...remark } })
          });
        }
      }
    ];

    const interventionAttachmentListeners: WebSocketMessageListener<InterventionAttachment>[] = [
      {
        model: "InterventionAttachment",
        action: "upsert",
        filter: { appointment_id },
        callback: message => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment?.interventions?.length) return;

          const attachment = message.data;
          const interventionIdx = appointment.interventions.findIndex(i => i.id === attachment.intervention_id);
          if (interventionIdx < 0) return;

          const intervention = appointment.interventions[interventionIdx];
          const attachmentIdx = intervention.attachments?.findIndex(a => a.id === attachment.id) ?? -1;

          const updatedAttachments = attachmentIdx >= 0 ? intervention.attachments?.with(attachmentIdx, attachment) : [...(intervention.attachments ?? []), attachment];

          typedQueryClient.setQueryData(queryKey, {
            ...appointment,
            interventions: appointment.interventions.with(interventionIdx, { ...intervention, attachments: updatedAttachments })
          });
        }
      },
      {
        model: "InterventionAttachment",
        action: "delete",
        filter: { appointment_id },
        callback: message => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment?.interventions?.length) return;

          const attachment = message.data;
          const interventionIdx = appointment.interventions.findIndex(i => i.id === attachment.intervention_id);
          if (interventionIdx < 0) return;

          const intervention = appointment.interventions[interventionIdx];
          const attachmentIdx = intervention.attachments?.findIndex(a => a.id === attachment.id) ?? -1;
          if (attachmentIdx < 0) return;

          typedQueryClient.setQueryData(queryKey, {
            ...appointment,
            interventions: appointment.interventions.with(interventionIdx, { ...intervention, attachments: intervention.attachments?.toSpliced(attachmentIdx, 1) })
          });
        }
      }
    ];

    const kioskRemarkListeners: WebSocketMessageListener<KioskRemark>[] = [
      {
        model: "KioskRemark",
        action: "upsert",
        filter: { appointment_id },
        callback: message => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment?.kiosk_communications?.length) return;

          const remark = message.data;
          const commIdx = appointment.kiosk_communications.findIndex(kc => kc.id === remark.kiosk_communication_id);
          if (commIdx < 0) return;

          const comm = appointment.kiosk_communications[commIdx];
          typedQueryClient.setQueryData(queryKey, {
            ...appointment,
            kiosk_communications: appointment.kiosk_communications.with(commIdx, { ...comm, remark: { ...comm.remark, ...remark } })
          });
        }
      }
    ];

    const appointmentStatusHistoryListeners: WebSocketMessageListener<AppointmentStatusHistory>[] = [
      {
        model: "AppointmentStatusHistory",
        action: "upsert",
        filter: { appointment_id },
        callback: ({ data: history }) => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment) return;

          const status_history = [...(appointment.status_history ?? [])];
          const historyIdx = status_history.findIndex(h => h.id === history.id);

          if (historyIdx > -1) status_history[historyIdx] = { ...status_history[historyIdx], ...history };
          else status_history.push(history);

          typedQueryClient.setQueryData(queryKey, { ...appointment, status_history });
        }
      }
    ];

    const customerCommunicationListeners: WebSocketMessageListener<CustomerCommunication>[] = [
      {
        model: "CustomerCommunication",
        filter: { appointment_id },
        action: "upsert",
        callback: message => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment) return;

          const customerCommunication = message.data;
          let updatedCustomerCommunication = customerCommunication;

          const correct_email = customerCommunication.correct_email || appointment.customer_communication?.correct_email || "";
          const correct_phone = customerCommunication.correct_phone || appointment.customer_communication?.correct_phone || "";

          if (appointment.customer_communication?.customer_communication_id === customerCommunication.customer_communication_id)
            updatedCustomerCommunication = { ...appointment.customer_communication, ...customerCommunication, correct_email, correct_phone };

          typedQueryClient.setQueryData(queryKey, { ...appointment, customer_communication: updatedCustomerCommunication });
        }
      }
    ];

    const communicationEventListeners: WebSocketMessageListener<CommunicationEvent>[] = [
      {
        model: "CommunicationEvent",
        filter: { appointment_id },
        action: "upsert",
        callback: message => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment?.customer_communication) return;

          const commEvent = message.data;
          const commEventIdx = appointment.customer_communication.events?.findIndex(e => e.id === commEvent.id) ?? -1;

          let updatedCommEvents = [...(appointment.customer_communication.events ?? []), commEvent];

          if (commEventIdx > -1 && appointment.customer_communication.events?.length) {
            updatedCommEvents = appointment.customer_communication.events.with(commEventIdx, {
              ...appointment.customer_communication.events[commEventIdx],
              ...commEvent
            });
          }

          typedQueryClient.setQueryData(queryKey, {
            ...appointment,
            customer_communication: { ...appointment.customer_communication, events: updatedCommEvents }
          });
        }
      }
    ];

    const checkInResultListeners: WebSocketMessageListener<CheckInResult>[] = [
      {
        model: "CheckInResult",
        action: "upsert",
        filter: { appointment_id },
        callback: message => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          const comm = appointment?.customer_communication;
          if (!comm) return;

          const result = message.data;
          const resultIdx = comm.check_in_results?.findIndex(r => r.id === result.id) ?? -1;

          let updatedResults = [...(comm.check_in_results ?? []), result];
          if (resultIdx > -1 && comm.check_in_results?.length) {
            if (!appointment.customer_communication) return;
            updatedResults = comm.check_in_results.with(resultIdx, { ...comm.check_in_results[resultIdx], ...result });
          }

          typedQueryClient.setQueryData(queryKey, { ...appointment, customer_communication: { ...comm, check_in_results: updatedResults } });
        }
      }
    ];

    const checkInRemarkListeners: WebSocketMessageListener<CheckInRemark>[] = [
      {
        model: "CheckInRemark",
        action: "upsert",
        filter: { appointment_id },
        callback: message => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          const comm = appointment?.customer_communication;
          const remark = message.data;
          if (!comm) return;

          if (comm.check_in_remarks?.some(checkInRemark => checkInRemark.id === remark.id)) return;

          typedQueryClient.setQueryData(queryKey, {
            ...appointment,
            customer_communication: { ...comm, check_in_remarks: [...(comm.check_in_remarks ?? []), remark] }
          });
        }
      }
    ];

    const communicationReceiverListeners: WebSocketMessageListener<CommunicationReceiver>[] = [
      {
        model: "CommunicationReceiver",
        action: "upsert",
        filter: { appointment_id },
        callback: message => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment?.customer_communication?.events?.length) return;

          const events = appointment.customer_communication.events;
          const receiver = message.data;
          const eventIdx = events.findIndex(e => e.id === receiver.communication_event_id);
          if (eventIdx < 0) return;

          const receivers = events[eventIdx].receivers;
          if (!receivers?.length) return;

          const receiverIdx = receivers.findIndex(r => r.id === receiver.id);
          if (receiverIdx < 0) return;

          typedQueryClient.setQueryData(queryKey, {
            ...appointment,
            customer_communication: {
              ...appointment.customer_communication,
              events: events.with(eventIdx, { ...events[eventIdx], receivers: receivers.with(receiverIdx, { ...receivers[receiverIdx], ...receiver }) })
            }
          });
        }
      }
    ];

    const customerCommunicationInterventionListeners: WebSocketMessageListener<CustomerCommunicationIntervention>[] = [
      {
        model: "CustomerCommunicationIntervention",
        action: "upsert",
        filter: { appointment_id },
        callback: ({ data: result }) => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment?.customer_communication?.events?.length) return;

          const events = appointment.customer_communication.events;
          const eventIdx = events.findIndex(e => e.id === result.communication_event_id);
          if (eventIdx < 0) return;

          const results = events[eventIdx].intervention_results;
          if (!results?.length) return;

          const resultIdx = results.findIndex(r => r.id === result.id);
          if (resultIdx < 0) return;

          typedQueryClient.setQueryData(queryKey, {
            ...appointment,
            customer_communication: {
              ...appointment.customer_communication,
              events: events.with(eventIdx, { ...events[eventIdx], intervention_results: results.with(resultIdx, { ...results[resultIdx], ...result }) })
            }
          });
        }
      }
    ];

    const customerCommunicationReplacementListeners: WebSocketMessageListener<CustomerCommunicationReplacement>[] = [
      {
        model: "CustomerCommunicationReplacement",
        action: "upsert",
        filter: { appointment_id },
        callback: ({ data: result }) => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment?.customer_communication?.events?.length) return;

          const events = appointment.customer_communication.events;
          const eventIdx = events.findIndex(e => e.id === result.communication_event_id);
          if (eventIdx < 0) return;

          const results = events[eventIdx].replacement_results;
          if (!results?.length) return;

          const resultIdx = results.findIndex(r => r.id === result.id);
          if (resultIdx < 0) return;

          typedQueryClient.setQueryData(queryKey, {
            ...appointment,
            customer_communication: {
              ...appointment.customer_communication,
              events: events.with(eventIdx, { ...events[eventIdx], replacement_results: results.with(resultIdx, { ...results[resultIdx], ...result }) })
            }
          });
        }
      }
    ];

    const communicationAgreementListeners: WebSocketMessageListener<CommunicationAgreement>[] = [
      {
        model: "CommunicationAgreement",
        action: "upsert",
        filter: { appointment_id },
        callback: message => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment?.customer_communication?.events?.length) return;

          const events = appointment.customer_communication.events;
          const agreement = message.data;
          const eventIdx = events.findIndex(e => e.id === agreement.communication_event_id);
          if (eventIdx < 0) return;

          const agreements = events[eventIdx].agreements;
          if (!agreements?.length) return;

          const agreementIdx = agreements.findIndex(r => r.id === agreement.id);
          if (agreementIdx < 0) return;

          typedQueryClient.setQueryData(queryKey, {
            ...appointment,
            customer_communication: {
              ...appointment.customer_communication,
              events: events.with(eventIdx, { ...events[eventIdx], agreements: agreements.with(agreementIdx, { ...agreements[agreementIdx], ...agreement }) })
            }
          });
        }
      }
    ];

    const diagnoseOverviewRemarkListeners: WebSocketMessageListener<DiagnoseOverviewRemark>[] = [
      {
        model: "DiagnoseOverviewRemark",
        action: "upsert",
        filter: { appointment_id },
        callback: message => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          const comm = appointment?.customer_communication;
          const remark = message.data;
          if (!comm) return;

          if (comm.diagnose_overview_remarks?.some(dgRemark => dgRemark.id === remark.id)) return;

          typedQueryClient.setQueryData(queryKey, {
            ...appointment,
            customer_communication: { ...comm, diagnose_overview_remarks: [...(comm.diagnose_overview_remarks ?? []), remark] }
          });
        }
      }
    ];

    const diagnoseOverviewAgreedResultListeners: WebSocketMessageListener<DiagnoseOverviewAgreedResult>[] = [
      {
        model: "DiagnoseOverviewAgreedResult",
        action: "upsert",
        filter: { appointment_id },
        callback: message => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment?.customer_communication?.events?.length) return;

          const events = appointment.customer_communication.events;
          const result = message.data;
          const eventIdx = events.findIndex(e => e.id === result.communication_event_id);
          if (eventIdx < 0) return;

          const results = events[eventIdx].diagnose_overview_agreed_results;
          if (!results?.length) return;

          const resultIdx = results.findIndex(r => r.id === result.id);
          if (resultIdx < 0) return;

          typedQueryClient.setQueryData(queryKey, {
            ...appointment,
            customer_communication: {
              ...appointment.customer_communication,
              events: events.with(eventIdx, { ...events[eventIdx], diagnose_overview_agreed_results: results.with(resultIdx, { ...results[resultIdx], ...result }) })
            }
          });
        }
      }
    ];

    const diagnoseOverviewDeclinedResultListeners: WebSocketMessageListener<DiagnoseOverviewDeclinedResult>[] = [
      {
        model: "DiagnoseOverviewDeclinedResult",
        action: "upsert",
        filter: { appointment_id },
        callback: message => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment?.customer_communication?.events?.length) return;

          const events = appointment.customer_communication.events;
          const result = message.data;
          const eventIdx = events.findIndex(e => e.id === result.communication_event_id);
          if (eventIdx < 0) return;

          const results = events[eventIdx].diagnose_overview_declined_results;
          if (!results?.length) return;

          const resultIdx = results.findIndex(r => r.id === result.id);
          if (resultIdx < 0) return;

          typedQueryClient.setQueryData(queryKey, {
            ...appointment,
            customer_communication: {
              ...appointment.customer_communication,
              events: events.with(eventIdx, { ...events[eventIdx], diagnose_overview_declined_results: results.with(resultIdx, { ...results[resultIdx], ...result }) })
            }
          });
        }
      }
    ];

    const diagnoseOverviewContactResultListeners: WebSocketMessageListener<DiagnoseOverviewContactResult>[] = [
      {
        model: "DiagnoseOverviewContactResult",
        action: "upsert",
        filter: { appointment_id },
        callback: message => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment?.customer_communication?.events?.length) return;

          const events = appointment.customer_communication.events;
          const result = message.data;
          const eventIdx = events.findIndex(e => e.id === result.communication_event_id);
          if (eventIdx < 0) return;

          const results = events[eventIdx].diagnose_overview_contact_results;
          if (!results?.length) return;

          const resultIdx = results.findIndex(r => r.id === result.id);
          if (resultIdx < 0) return;

          typedQueryClient.setQueryData(queryKey, {
            ...appointment,
            customer_communication: {
              ...appointment.customer_communication,
              events: events.with(eventIdx, { ...events[eventIdx], diagnose_overview_contact_results: results.with(resultIdx, { ...results[resultIdx], ...result }) })
            }
          });
        }
      }
    ];

    const carNoteListeners: WebSocketMessageListener<CarNote>[] = [
      {
        model: "CarNote",
        action: "upsert",
        callback: message => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment?.car) return;

          const carNote = message.data;
          if (appointment.car_id !== carNote.car_id) return;

          const carNotes = [...(appointment.car.notes ?? [])];
          const carNoteIdx = carNotes.findIndex(n => n.id === carNote.id);

          let updatedCarNotes = [...carNotes, carNote];
          if (carNoteIdx > -1) updatedCarNotes = carNotes.with(carNoteIdx, { ...carNotes[carNoteIdx], ...carNote });

          typedQueryClient.setQueryData(queryKey, { ...appointment, car: { ...appointment.car, notes: updatedCarNotes } });
        }
      },
      {
        model: "CarNote",
        action: "delete",
        callback: message => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment?.car?.notes?.length) return;

          const carNote = message.data;
          const appCarNotes = appointment.car.notes;
          const carNoteIdx = appCarNotes.findIndex(n => n.id === carNote.id);
          if (carNoteIdx < 0) return;

          typedQueryClient.setQueryData(queryKey, { ...appointment, car: { ...appointment.car, notes: appCarNotes.toSpliced(carNoteIdx, 1) } });
        }
      }
    ];

    const carNoteAttachmentListeners: WebSocketMessageListener<CarNoteAttachment>[] = [
      {
        model: "CarNoteAttachment",
        action: "delete",
        callback: ({ data: attachment }) => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment?.car?.notes?.length) return;

          const noteIdx = appointment.car.notes.findIndex(n => n.id === attachment.car_note_id);
          if (noteIdx < 0) return;

          const note = appointment.car.notes[noteIdx];
          const attachmentIdx = note.attachments?.findIndex(a => a.id === attachment.id) ?? -1;
          if (attachmentIdx < 0) return;

          typedQueryClient.setQueryData(queryKey, {
            ...appointment,
            car: { ...appointment.car, notes: appointment.car.notes.with(noteIdx, { ...note, attachments: note.attachments?.toSpliced(attachmentIdx, 1) }) }
          });
        }
      }
    ];

    const accessCommunicationListeners: WebSocketMessageListener<AcsesCommunication>[] = [
      {
        model: "AcsesCommunication",
        filter: { appointment_id },
        action: "upsert",
        callback: message => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment) return;

          const acsesCommunication = message.data;

          const updatedAcsesCustomerCommunication = appointment.acses_customer_communication
            ? { ...appointment.acses_customer_communication, ...acsesCommunication }
            : acsesCommunication;

          typedQueryClient.setQueryData(queryKey, { ...appointment, acses_customer_communication: updatedAcsesCustomerCommunication });
        }
      }
    ];

    const acsesCommunicationReceiverListeners: WebSocketMessageListener<AcsesCommunicationReceiver>[] = [
      {
        model: "AcsesCommunicationReceiver",
        action: "upsert",
        filter: { appointment_id },
        callback: message => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment?.acses_customer_communication?.events?.length) return;

          const events = appointment.acses_customer_communication.events;
          const receiver = message.data;

          const eventIdx = events.findIndex(e => e.id === receiver.acses_communication_event_id);
          if (eventIdx < 0) return;
          const receivers = events[eventIdx].receivers;
          if (!receivers?.length) return;
          const receiverIdx = receivers.findIndex(r => r.id === receiver.id);
          if (receiverIdx < 0) return;

          typedQueryClient.setQueryData(queryKey, {
            ...appointment,
            acses_customer_communication: {
              ...appointment.acses_customer_communication,
              events: events.with(eventIdx, { ...events[eventIdx], receivers: receivers.with(receiverIdx, { ...receivers[receiverIdx], ...receiver }) })
            }
          });
        }
      }
    ];

    const kioskCommunicationListeners: WebSocketMessageListener<KioskCommunication>[] = [
      {
        model: "KioskCommunication",
        action: "upsert",
        filter: { appointment_id },
        callback: message => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment) return;

          const kioskComm = message.data;
          const kioskCommIdx = appointment.kiosk_communications?.findIndex(k => k.id === kioskComm.id) ?? -1;

          let updatedKioskCommunication = [...(appointment.kiosk_communications ?? []), kioskComm];
          if (kioskCommIdx > -1 && appointment.kiosk_communications?.length)
            updatedKioskCommunication = appointment.kiosk_communications.with(kioskCommIdx, { ...appointment.kiosk_communications[kioskCommIdx], ...kioskComm });

          typedQueryClient.setQueryData(queryKey, { ...appointment, kiosk_communications: updatedKioskCommunication });
        }
      }
    ];

    const deskCommunicationEventListeners: WebSocketMessageListener<DeskCommunicationEvent>[] = [
      {
        model: "DeskCommunicationEvent",
        action: "upsert",
        callback: ({ data: deskCommunicationEvent }) => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment?.desk_communications?.length) return;

          const deskCommIdx = appointment.desk_communications.findIndex(dc => dc.id === deskCommunicationEvent.desk_communication_id);
          if (deskCommIdx < 0) return;

          const eventIdx = appointment.desk_communications[deskCommIdx].events?.findIndex(event => event.id === deskCommunicationEvent.id) ?? -1;

          typedQueryClient.setQueryData(queryKey, {
            ...appointment,
            desk_communications: appointment.desk_communications.with(deskCommIdx, {
              ...appointment.desk_communications[deskCommIdx],
              events:
                eventIdx > -1 && appointment.desk_communications[deskCommIdx].events
                  ? appointment.desk_communications[deskCommIdx].events.with(eventIdx, deskCommunicationEvent)
                  : [...(appointment.desk_communications[deskCommIdx].events ?? []), deskCommunicationEvent]
            })
          });
        }
      }
    ];

    const deskCommunicationListeners: WebSocketMessageListener<DeskCommunication>[] = [
      {
        model: "DeskCommunication",
        action: "upsert",
        filter: { appointment_id },
        callback: message => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment) return;

          const deskCom = message.data;
          const deskComIdx = appointment.desk_communications?.findIndex(dc => dc.id === deskCom.id) ?? -1;

          let updatedDeskCommunication = [...(appointment.desk_communications ?? []), deskCom];
          if (deskComIdx > -1 && appointment.desk_communications?.length)
            updatedDeskCommunication = appointment.desk_communications.with(deskComIdx, { ...appointment.desk_communications[deskComIdx], ...deskCom });

          typedQueryClient.setQueryData(queryKey, {
            ...appointment,
            desk_communications: updatedDeskCommunication,
            ...(deskCom.correct_email && { customcom_correct_email: deskCom.correct_email }),
            ...(deskCom.correct_phone && { customcom_correct_phone: deskCom.correct_phone })
          });
        }
      }
    ];

    const checkEventListeners: WebSocketMessageListener<CarCheckEvent>[] = [
      {
        model: "CarCheckEvent",
        action: "upsert",
        filter: { appointment_id },
        callback: message => {
          const checkEvent = message.data;
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment || appointment.car_check_events?.some(ce => ce.id === checkEvent.id)) return;

          typedQueryClient.setQueryData(queryKey, {
            ...appointment,
            car_check_events: appointment.car_check_events ? [...appointment.car_check_events, checkEvent] : [checkEvent]
          });
        }
      }
    ];

    const kioskCommunicationEventListeners: WebSocketMessageListener<KioskCommunicationEvent>[] = [
      {
        model: "KioskCommunicationEvent",
        action: "upsert",
        callback: message => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment?.kiosk_communications?.length) return;

          const event = message.data;
          const eventIdx = appointment.kiosk_communications.findIndex(kc => kc.id === event.kiosk_communication_id);
          if (eventIdx < 0) return;

          const events = [...(appointment.kiosk_communications[eventIdx].events ?? []), event];

          typedQueryClient.setQueryData(queryKey, {
            ...appointment,
            kiosk_communications: appointment.kiosk_communications.with(eventIdx, { ...appointment.kiosk_communications[eventIdx], events })
          });
        }
      }
    ];

    const communicationResultListeners: WebSocketMessageListener<CommunicationResult>[] = [
      {
        model: "CommunicationResult",
        action: "upsert",
        callback: message => {
          const communicationResult = message.data;
          const appointment = typedQueryClient.getQueryData(queryKey);

          if (!appointment?.customer_communication?.diagnose_overview_results?.length) return;

          const diagnoseOverviewResultIdx = appointment.customer_communication.diagnose_overview_results.findIndex(
            result => result.question_result_id === communicationResult.question_result_id
          );

          if (diagnoseOverviewResultIdx < 0) return;

          const updatedDiagnoseOverviewResult = {
            ...appointment.customer_communication.diagnose_overview_results[diagnoseOverviewResultIdx],
            ...communicationResult
          };

          const updatedDiagnoseOverviewResults = appointment.customer_communication.diagnose_overview_results.with(
            diagnoseOverviewResultIdx,
            updatedDiagnoseOverviewResult
          );

          typedQueryClient.setQueryData(queryKey, {
            ...appointment,
            customer_communication: {
              ...appointment.customer_communication,
              diagnose_overview_results: updatedDiagnoseOverviewResults
            }
          });
        }
      }
    ];

    const appointmentLogListeners: WebSocketMessageListener<AppointmentLog>[] = [
      {
        model: "AppointmentLog",
        action: "upsert",
        filter: { appointment_id },
        callback: message => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment) return;

          const log = message.data; // TODO: cleanup all similar assignment that are done just to rename, instead directly name it in the function param
          const appLogIdx = appointment.logs?.findIndex(l => l.id === log.id) ?? -1;

          const updatedLogData = appLogIdx > -1 ? appointment.logs?.with(appLogIdx, { ...appointment.logs[appLogIdx], ...log }) : [...(appointment.logs ?? []), log];
          typedQueryClient.setQueryData(queryKey, { ...appointment, logs: updatedLogData });
        }
      }
    ];

    const pendingChecklistsListeners: WebSocketMessageListener<InterventionPendingChecklist>[] = [
      {
        model: "InterventionPendingChecklist",
        action: "upsert",
        filter: { appointment_id },
        callback: ({ data: pendingChecklist }) => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment || !appointment.interventions) return;

          const interventionIdx = appointment.interventions.findIndex(intervention => intervention.id === pendingChecklist.intervention_id);
          if (interventionIdx < 0) return;

          const intervention = appointment.interventions[interventionIdx];
          const pendingChecklistIdx = intervention.pending_checklists?.findIndex(p => p.checklist_id === pendingChecklist.checklist_id) ?? -1;
          const updatePendingChecklists = [...(intervention.pending_checklists || [])];

          if (pendingChecklistIdx < 0) updatePendingChecklists.push(pendingChecklist);
          else updatePendingChecklists[pendingChecklistIdx] = { ...updatePendingChecklists[pendingChecklistIdx], ...pendingChecklist };

          typedQueryClient.setQueryData(queryKey, {
            ...appointment,
            interventions: appointment.interventions.with(interventionIdx, { ...intervention, pending_checklists: updatePendingChecklists })
          });
        }
      },
      {
        model: "InterventionPendingChecklist",
        action: "delete",
        filter: { appointment_id },
        callback: ({ data: pendingChecklist }) => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment || !appointment.interventions?.length) return;

          const interventionIdx = appointment.interventions.findIndex(intervention => intervention.id === pendingChecklist.intervention_id);
          if (interventionIdx < 0) return;

          const intervention = appointment.interventions[interventionIdx];
          if (!intervention.pending_checklists?.length) return;

          const pendingChecklistIdx = intervention.pending_checklists.findIndex(p => p.checklist_id === pendingChecklist.checklist_id);
          if (pendingChecklistIdx < 0) return;

          typedQueryClient.setQueryData(queryKey, {
            ...appointment,
            interventions: appointment.interventions.with(interventionIdx, {
              ...intervention,
              pending_checklists: intervention.pending_checklists.toSpliced(pendingChecklistIdx, 1).map((checklist, idx) => ({ ...checklist, ordering: idx + 1 }))
            })
          });
        }
      }
    ];

    const checklistListeners: WebSocketMessageListener<Checklist>[] = [
      {
        model: "Checklist",
        action: "upsert",
        callback: ({ data: checklist }) => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment || !appointment.interventions?.length) return;

          const hasPendingChecklist = appointment.interventions.some(intervention => intervention.pending_checklists?.some(pc => pc.checklist_id === checklist.id));
          if (!hasPendingChecklist) return;

          typedQueryClient.setQueryData(queryKey, {
            ...appointment,
            interventions: appointment.interventions.map(intervention => {
              const pendingChecklistIdx = intervention.pending_checklists?.findIndex(pc => pc.checklist_id === checklist.id) ?? -1;
              if (!intervention.pending_checklists || pendingChecklistIdx === -1) return intervention;

              return {
                ...intervention,
                pending_checklists: intervention.pending_checklists.with(pendingChecklistIdx, {
                  ...intervention.pending_checklists[pendingChecklistIdx],
                  checklist_name: checklist.name
                })
              };
            })
          });
        }
      }
    ];

    const customerNoteListeners: WebSocketMessageListener<CustomerNote>[] = [
      {
        model: "CustomerNote",
        action: "upsert",
        callback: ({ data: customerNote }) => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment) return;

          const updatedCustomer: UpdateCustomerPayload = {
            ...(appointment.customer_owner?.id === customerNote.customer_id && { customer_owner: getUpdatedCustomer(appointment.customer_owner, customerNote) }),
            ...(appointment.customer_driver?.id === customerNote.customer_id && { customer_driver: getUpdatedCustomer(appointment.customer_driver, customerNote) }),
            ...(appointment.customer_contract?.id === customerNote.customer_id && { customer_contract: getUpdatedCustomer(appointment.customer_contract, customerNote) })
          };

          if (!Object.keys(updatedCustomer).length) return;
          typedQueryClient.setQueryData(queryKey, { ...appointment, ...updatedCustomer });
        }
      },
      {
        model: "CustomerNote",
        action: "delete",
        callback: ({ data: customerNote }) => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment) return;

          const updatedCustomer: UpdateCustomerPayload = {
            ...(appointment.customer_owner?.id === customerNote.customer_id && { customer_owner: deleteCustomerNote(appointment.customer_owner, customerNote.id) }),
            ...(appointment.customer_driver?.id === customerNote.customer_id && { customer_driver: deleteCustomerNote(appointment.customer_driver, customerNote.id) }),
            ...(appointment.customer_contract?.id === customerNote.customer_id && {
              customer_contract: deleteCustomerNote(appointment.customer_contract, customerNote.id)
            })
          };

          if (!Object.keys(updatedCustomer).length || some(updatedCustomer, isUndefined)) return;
          typedQueryClient.setQueryData(queryKey, { ...appointment, ...updatedCustomer });
        }
      }
    ];

    const customerListeners: WebSocketMessageListener<Customer>[] = [
      {
        model: "Customer",
        action: "upsert",
        callback: ({ data: customer }) => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment) return;

          if (appointment.customer_owner?.id === customer.id)
            typedQueryClient.setQueryData(queryKey, { ...appointment, customer_owner: { ...appointment.customer_owner, ...customer } });
          if (appointment.customer_driver?.id === customer.id)
            typedQueryClient.setQueryData(queryKey, { ...appointment, customer_driver: { ...appointment.customer_driver, ...customer } });
          if (appointment.customer_contract?.id === customer.id)
            typedQueryClient.setQueryData(queryKey, { ...appointment, customer_contract: { ...appointment.customer_contract, ...customer } });
        }
      }
    ];

    const snoozeListeners: WebSocketMessageListener<Snooze>[] = [
      {
        model: "Snooze",
        action: "upsert",
        filter: { appointment_id },
        callback: ({ data: snooze }) => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          const interventions = appointment?.interventions;

          if (appointment && interventions?.length) {
            const interventionIdx = interventions.findIndex(({ id }) => id === snooze.intervention_id);
            if (interventionIdx > -1) {
              const intervention = interventions[interventionIdx];
              const snoozeItemHistoryIdx = intervention.snooze_history?.findIndex(({ id }) => id === snooze.id) ?? -1;

              const updatedSnoozeHistory =
                snoozeItemHistoryIdx === -1
                  ? [snooze, ...(intervention.snooze_history ?? [])]
                  : intervention.snooze_history?.with(snoozeItemHistoryIdx, { ...intervention.snooze_history[snoozeItemHistoryIdx], ...snooze });

              if (updatedSnoozeHistory)
                typedQueryClient.setQueryData(queryKey, {
                  ...appointment,
                  interventions: interventions.with(interventionIdx, { ...intervention, snoozed: true, snooze_history: updatedSnoozeHistory })
                });
            }
          }

          const checks = typedQueryClient.getQueryData(AppointmentDetailsKeys.checks);
          if (!checks?.length) return;

          const checkIdx = checks.findIndex(check => check.question_items?.some(qr => qr.id === snooze.question_result_id));
          if (checkIdx < 0 || !checks[checkIdx].question_items?.length) return;

          const questionIdx = checks[checkIdx].question_items.findIndex(qr => qr.id === snooze.question_result_id);
          if (questionIdx < 0) return;

          let snoozeHistory = [...(checks[checkIdx].question_items[questionIdx].snooze_history ?? [])];
          const snoozeIdx = snoozeHistory.findIndex(s => s.id === snooze.id);
          snoozeHistory = snoozeIdx === -1 ? [snooze, ...snoozeHistory] : snoozeHistory.with(snoozeIdx, { ...snoozeHistory[snoozeIdx], ...snooze });

          typedQueryClient.setQueryData(
            AppointmentDetailsKeys.checks,
            checks.with(checkIdx, {
              ...checks[checkIdx],
              question_items: checks[checkIdx].question_items.with(questionIdx, {
                ...checks[checkIdx].question_items[questionIdx],
                snooze_history: snoozeHistory,
                snoozed: true
              })
            })
          );
        }
      },
      {
        model: "Snooze",
        action: "delete",
        filter: { appointment_id },
        callback: ({ data: snooze }) => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          const interventions = appointment?.interventions;

          if (appointment && interventions?.length) {
            const interventionIdx = interventions.findIndex(({ id }) => id === snooze.intervention_id);
            if (interventionIdx > -1)
              typedQueryClient.setQueryData(queryKey, {
                ...appointment,
                interventions: interventions.with(interventionIdx, { ...interventions[interventionIdx], snoozed: false, snooze_history: [] })
              });
          }

          const checks = typedQueryClient.getQueryData(AppointmentDetailsKeys.checks);
          if (!checks?.length) return;

          const checkIdx = checks.findIndex(check => check.question_items?.some(item => item.id === snooze.question_result_id));
          if (checkIdx < 0 || !checks[checkIdx].question_items?.length) return;

          const questions = checks[checkIdx].question_items;
          const questionIdx = questions.findIndex(qi => qi.id === snooze.question_result_id);
          if (questionIdx < 0 || !questions[questionIdx].snooze_history?.length) return;

          const snoozeIdx = questions[questionIdx].snooze_history.findIndex(s => s.id === snooze.id);
          if (snoozeIdx < 0) return;

          const snoozes = questions[questionIdx].snooze_history.toSpliced(snoozeIdx, 1);
          typedQueryClient.setQueryData(
            AppointmentDetailsKeys.checks,
            checks.with(checkIdx, {
              ...checks[checkIdx],
              question_items: questions.with(questionIdx, { ...questions[questionIdx], snooze_history: snoozes, snoozed: snoozes.length > 0 })
            })
          );
        }
      }
    ];

    const vehicleTyreListeners: WebSocketMessageListener<VehicleTyre>[] = [
      {
        model: "VehicleTyre",
        action: "upsert",
        callback: ({ data: vehicleTyre }) => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment?.car || appointment.car.id !== vehicleTyre.car_id) return;

          const tyres = [...(appointment.car.tyres ?? [])];
          const tyreIdx = tyres.findIndex(t => t.id === vehicleTyre.id);

          if ([TYRE_LOCATIONS.RemovedFromVehicle, TYRE_LOCATIONS.RemovedFromStorage].includes(vehicleTyre.location)) {
            if (tyreIdx < 0) return;
          } else {
            const previousTyreIdx = tyres.findIndex(
              t =>
                ((t.location === TYRE_LOCATIONS.RemovedFromVehicle && vehicleTyre.location === TYRE_LOCATIONS.OnVehicle) ||
                  (t.location === TYRE_LOCATIONS.RemovedFromStorage && vehicleTyre.location === TYRE_LOCATIONS.InStorage)) &&
                t.axle === vehicleTyre.axle &&
                t.position === vehicleTyre.position
            );
            if (previousTyreIdx > -1) {
              vehicleTyre.replacements = tyres[previousTyreIdx].replacements;
              tyres.splice(previousTyreIdx, 1);
            }
          }

          if (tyreIdx < 0) tyres.push(vehicleTyre);
          else tyres[tyreIdx] = { ...tyres[tyreIdx], ...vehicleTyre };

          typedQueryClient.setQueryData(queryKey, { ...appointment, car: { ...appointment.car, tyres } });
        }
      }
    ];

    const vehicleTyreReplacementListeners: WebSocketMessageListener<VehicleTyreReplacement>[] = [
      {
        model: "VehicleTyreReplacement",
        action: "upsert",
        filter: { appointment_id },
        callback: ({ data: replacement }) => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment?.car?.tyres?.length) return;

          const tyreIdx = appointment.car.tyres.findIndex(
            t => t.location === TYRE_LOCATIONS.OnVehicle && t.axle === replacement.axle && t.position === replacement.position
          );
          if (tyreIdx < 0) return;

          const replacements = appointment.car.tyres[tyreIdx].replacements ?? [];
          const replacementIdx = replacements.findIndex(r => r.id === replacement.id);

          const interventions = [...(appointment.interventions ?? [])];
          if (interventions.length) {
            if (replacement.intervention_id) {
              const interventionIdx = interventions.findIndex(i => i.id === replacement.intervention_id);
              if (interventionIdx > -1) {
                const replacementIdx = interventions[interventionIdx].replacements?.findIndex(r => r.id === replacement.id) ?? -1;
                if (replacementIdx < 0) {
                  interventions[interventionIdx] = {
                    ...interventions[interventionIdx],
                    replacements: (interventions[interventionIdx].replacements ?? []).concat(replacement)
                  };
                } else if (interventions[interventionIdx].replacements) {
                  interventions[interventionIdx] = {
                    ...interventions[interventionIdx],
                    replacements: interventions[interventionIdx].replacements.with(replacementIdx, {
                      ...interventions[interventionIdx].replacements[replacementIdx],
                      ...replacement
                    })
                  };
                }
              }
            } else {
              const interventionIdx = interventions.findIndex(i => i.replacements?.some(r => r.id === replacement.id));
              if (interventionIdx > -1 && interventions[interventionIdx].replacements) {
                const replacementIdx = interventions[interventionIdx].replacements.findIndex(r => r.id === replacement.id);
                if (replacementIdx > -1)
                  interventions[interventionIdx] = {
                    ...interventions[interventionIdx],
                    replacements: interventions[interventionIdx].replacements.toSpliced(replacementIdx, 1)
                  };
              }
            }
          }

          typedQueryClient.setQueryData(queryKey, {
            ...appointment,
            car: {
              ...appointment.car,
              tyres: appointment.car.tyres.with(tyreIdx, {
                ...appointment.car.tyres[tyreIdx],
                replacements:
                  replacementIdx < 0 ? replacements.concat(replacement) : replacements.with(replacementIdx, { ...replacements[replacementIdx], ...replacement })
              })
            },
            interventions
          });
        }
      },
      {
        model: "VehicleTyreReplacement",
        action: "delete",
        filter: { appointment_id },
        callback: ({ data: replacement }) => {
          const appointment = typedQueryClient.getQueryData(queryKey);
          if (!appointment?.car?.tyres?.length) return;

          const tyreIdx = appointment.car.tyres.findIndex(t => t.replacements?.some(r => r.id === replacement.id));
          if (tyreIdx < 0 || !appointment.car.tyres[tyreIdx].replacements?.length) return;

          const replacementIdx = appointment.car.tyres[tyreIdx].replacements.findIndex(r => r.id === replacement.id);
          if (replacementIdx < 0) return;

          const interventions = appointment.interventions ?? [];
          if (replacement.intervention_id && interventions.length) {
            const interventionIdx = interventions.findIndex(i => i.id === replacement.intervention_id);
            if (interventionIdx > -1 && interventions[interventionIdx].replacements?.length) {
              const replacementIdx = interventions[interventionIdx].replacements.findIndex(r => r.id === replacement.id);
              if (replacementIdx > -1)
                interventions[interventionIdx] = {
                  ...interventions[interventionIdx],
                  replacements: interventions[interventionIdx].replacements.toSpliced(replacementIdx, 1)
                };
            }
          }

          typedQueryClient.setQueryData(queryKey, {
            ...appointment,
            car: {
              ...appointment.car,
              tyres: appointment.car.tyres.with(tyreIdx, {
                ...appointment.car.tyres[tyreIdx],
                replacements: appointment.car.tyres[tyreIdx].replacements.toSpliced(replacementIdx, 1)
              })
            },
            interventions
          });
        }
      }
    ];

    return [
      ...appointmentListeners,
      ...interventionElementListeners,
      ...carListeners,
      ...pinListeners,
      ...appointmentNoteListeners,
      ...appointmentNoteAttachmentListeners,
      ...interventionListeners,
      ...keyLockerCommunicationListeners,
      ...keyLockerCommunicationEventListeners,
      ...keyLockerCommunicationReceiverListeners,
      ...keyLockerRemarkListeners,
      ...kioskRemarkListeners,
      ...appointmentStatusHistoryListeners,
      ...customerCommunicationListeners,
      ...communicationEventListeners,
      ...checkInResultListeners,
      ...checkInRemarkListeners,
      ...communicationReceiverListeners,
      ...customerCommunicationInterventionListeners,
      ...customerCommunicationReplacementListeners,
      ...communicationAgreementListeners,
      ...diagnoseOverviewRemarkListeners,
      ...diagnoseOverviewAgreedResultListeners,
      ...diagnoseOverviewDeclinedResultListeners,
      ...diagnoseOverviewContactResultListeners,
      ...carNoteListeners,
      ...carNoteAttachmentListeners,
      ...accessCommunicationListeners,
      ...acsesCommunicationReceiverListeners,
      ...kioskCommunicationListeners,
      ...deskCommunicationEventListeners,
      ...deskCommunicationListeners,
      ...checkEventListeners,
      ...kioskCommunicationEventListeners,
      ...communicationResultListeners,
      ...appointmentLogListeners,
      ...interventionAttachmentListeners,
      ...pendingChecklistsListeners,
      ...checklistListeners,
      ...customerNoteListeners,
      ...customerListeners,
      ...snoozeListeners,
      ...vehicleTyreListeners,
      ...vehicleTyreReplacementListeners
    ];
  }, [typedQueryClient, appointment_id]);

  return listeners;
};
